import type { Variables } from "graphql-request"
import { isEmptyObject } from "@myvp/shared/src/functions/is-empty-object"

export interface GraphqlInputSanitizationMap {
  operations: Map<string, Record<string, string>>
  types: Map<string, Record<string, string | null>>
}

const globalOperations: GraphqlInputSanitizationMap["operations"] = new Map()
const globalTypes: GraphqlInputSanitizationMap["types"] = new Map()

/**
 * Registers a map of operation names and their referenced input types.
 * This is used by apps to populate the shared client's knowledge of their specific operations.
 */
export const registerOperationVariables = (map: {
  operations: Record<string, Record<string, string>>
  types: Record<string, Record<string, string | null>>
}) => {
  if (map.operations) {
    for (const [name, variables] of Object.entries(map.operations)) {
      globalOperations.set(name, variables)
    }
  }
  if (map.types) {
    for (const [name, fields] of Object.entries(map.types)) {
      globalTypes.set(name, fields)
    }
  }
}

function sanitizeObject(
  typeName: string,
  data: unknown,
  typesMap: Map<string, Record<string, string | null>>
): unknown {
  if (!data || typeof data !== "object") {
    return data
  } else if (Array.isArray(data)) {
    return data.map((item) => sanitizeObject(typeName, item, typesMap))
  }

  const typeInfo = typesMap.get(typeName)
  // If we don't have information about this type, we can't safely sanitize it,
  if (!typeInfo) {
    if (process.env.NODE_ENV !== "production") {
      throw new Error(`Unknown graphql input type: ${typeName}`)
    }
    return data
  }

  const sanitized: Record<string, unknown> = {}
  for (const key in data) {
    if (!Object.hasOwn(data, key)) continue
    const value = (data as Record<string, unknown>)[key]
    if (key in typeInfo) {
      const nestedTypeName = typeInfo[key]
      if (!nestedTypeName || !value) {
        sanitized[key] = value
      } else {
        sanitized[key] = sanitizeObject(nestedTypeName, value, typesMap)
      }
    }
  }
  return sanitized
}

/**
 * Sanitizes GraphQL variables by stripping unexpected fields based on the schema's input types.
 * Use build-time operation maps to identify variable types.
 *
 * @param operationName The name of the GraphQL operation (query/mutation).
 * @param variables The variables object to sanitize.
 * @param localSanitizationMap Optional local sanitization map (must be Map-based).
 */
export function sanitizeGraphQLInput<V extends Variables | undefined>(
  operationName: string | undefined,
  variables: V,
  localSanitizationMap?: GraphqlInputSanitizationMap
): V {
  if (!variables || typeof variables !== "object" || !operationName) {
    return variables
  }

  const operations = localSanitizationMap?.operations ?? globalOperations
  const types = localSanitizationMap?.types ?? globalTypes

  const variableTypes = operations.get(operationName)
  // If we don't have operation mapping for this operation, we can't safely sanitize it.
  if (!variableTypes) {
    if (
      process.env.NODE_ENV !== "production" &&
      // allow empty objects to ignore the devtime warning because graphql-request
      // allows passing `{}` when there are no inputs.
      !isEmptyObject(variables)
    ) {
      throw new Error(`Unknown graphql operation name: ${operationName}`)
    }
    return variables
  }

  const sanitizedVariables = {} as NonNullable<V>

  for (const [key, value] of Object.entries(variables)) {
    const typeName = variableTypes[key]
    if (!typeName || !value) {
      // NOTE: BE graphql doesn't actually care if an extra top level variable is sent.
      sanitizedVariables[key] = value
    } else {
      sanitizedVariables[key] = sanitizeObject(typeName, value, types)
    }
  }

  return sanitizedVariables
}

/**
 * A graphql-request middleware that automatically sanitizes variables before the request is sent.
 * It parses the request body, strips unexpected fields from variables, and re-stringifies the body.
 */
export const sanitizeGraphQLInputMiddleware = <
  // this is meant to match `RequestExtendedInit<Variables>` from `graphql-request`,
  // but it isn't exported and deriving it is a bit unwieldy for just this case.
  T extends {
    body?: unknown
    operationName?: string
    variables?: Variables
  },
>(
  request: T,
  localSanitizationMap?: GraphqlInputSanitizationMap
): T => {
  if (typeof request.body === "string") {
    const variables = sanitizeGraphQLInput(
      request.operationName,
      request.variables,
      localSanitizationMap
    )
    let body: {
      operationName: string
      query: string
      variables: typeof variables
    }
    try {
      body = JSON.parse(request.body)
    } catch (error) {
      // If parsing fails, we just continue with the original request
      // oxlint-disable-next-line no-console
      console.warn(error)
      return request
    }
    // oxlint-disable-next-line no-param-reassign
    request.body = JSON.stringify({ ...body, variables })
    // oxlint-disable-next-line no-param-reassign
    request.variables = variables
  }
  return request
}
